import React from 'react';
import {Show, useRecordContext, useTranslate} from 'react-admin';
import {Link as RouterLink} from 'react-router-dom';
import {Avatar, Box, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import MonoChip from '../components/statamic/MonoChip';
import {formatDateTime} from '../lib/dates';

const safePrettyJson = (raw) => {
    if (raw == null) return null;
    if (typeof raw !== 'string') {
        try { return JSON.stringify(raw, null, 2); } catch { return String(raw); }
    }
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
};

const UserLink = ({user}) => {
    if (!user) return null;
    const avatarUrl = user.avatar ? `${process.env.REACT_APP_CDN_URL}/${user.avatar}` : undefined;
    const fullName = [user.given_name, user.family_name].filter(Boolean).join(' ') || user.email || 'Unnamed';
    const to = user.is_volunteer ? `/volunteers/${user.id}/show` : `/guardians/${user.id}/show`;

    return (
        <Box
            component={RouterLink}
            to={to}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                textDecoration: 'none',
                color: 'inherit',
                p: 0.5,
                borderRadius: `${t.radius.sm}px`,
                '&:hover': {backgroundColor: t.color.sidebarHover},
            }}
        >
            <Avatar
                src={avatarUrl}
                sx={{
                    width: 36, height: 36,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                    fontSize: 13,
                }}
            >
                {(user.given_name?.charAt(0) ?? '') + (user.family_name?.charAt(0) ?? '')}
            </Avatar>
            <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.accent.primary, fontWeight: 500}}>
                {fullName}
            </Typography>
        </Box>
    );
};

const CodeBlock = ({children}) => (
    <Box
        component="pre"
        sx={{
            margin: 0,
            padding: 2,
            borderRadius: `${t.radius.sm}px`,
            backgroundColor: t.color.chip?.monoBg ?? t.color.sidebarHover,
            border: `1px solid ${t.color.border.hairline}`,
            color: t.color.text.primary,
            fontFamily: t.font.mono,
            fontSize: `${t.type.caption.size}px`,
            whiteSpace: 'pre',
            overflowX: 'auto',
            lineHeight: 1.5,
        }}
    >
        {children ?? '—'}
    </Box>
);

const AppErrorsShowContent = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    const prettyMessage = safePrettyJson(record.message);
    const createdAt = formatDateTime(record.create_date_time ?? record.date);

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('App Errors'), to: '/errors'}}
                title={
                    <Box component="span">
                        {record.action || record.screen || `Error #${record.id}`}
                    </Box>
                }
                subtitle={createdAt ?? undefined}
            />

            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <SectionCard title={translate('Reporter')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0, alignItems: 'center'}}>
                        <DataRow label="User">
                            {record.user ? <UserLink user={record.user}/> : null}
                        </DataRow>
                        <DataRow label={translate('Date')} value={createdAt}/>
                    </Box>
                </SectionCard>

                <SectionCard title={translate('Details')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 3, rowGap: 0}}>
                        <DataRow label="Platform">
                            {record.platform ? <MonoChip>{record.platform}</MonoChip> : null}
                        </DataRow>
                        <DataRow label="Screen" value={record.screen}/>
                        <DataRow label="Action" value={record.action}/>
                    </Box>
                </SectionCard>

                <SectionCard title="Error">
                    <CodeBlock>{prettyMessage}</CodeBlock>
                </SectionCard>
            </Box>
        </Box>
    );
};

const AppErrorsShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <AppErrorsShowContent/>
    </Show>
);

export default AppErrorsShowStatamic;
